import React, { useState } from 'react';
import { enhanceImageUrl } from '../utils/imageEnhancer';

const VisionBoard = ({ 
  affirmationIndex, 
  userImages, 
  side = 'both', // 'left', 'right', 'both', or 'grid'
  offsets = [0, 1, 2, 3], // which image positions to show
  className = '',
  showEmptyBoxes = true,
  allowExpand = false // Enable click-to-expand for meditation
}) => {
  
  const [expandedImage, setExpandedImage] = useState(null);
  
  // Single vision image component - ENHANCED RESOLUTION & CLICKABILITY
  const VisionImage = ({ offset }) => {
    const imageIndex = affirmationIndex * 4 + offset;
    const image = userImages?.[imageIndex];
    
    return (
      <div className="w-full">
        {image ? (
          <div 
            className={`bg-white/10 backdrop-blur-lg rounded-2xl p-3 border border-white/20 group ${allowExpand ? 'cursor-pointer hover:bg-white/20 transition-all duration-300 hover:scale-105' : ''}`}
            onClick={allowExpand ? () => setExpandedImage({ url: image, index: imageIndex }) : undefined}
          >
            <div className="relative">
              <img 
                src={enhanceImageUrl(image)} 
                alt={`Vision ${imageIndex + 1}`}
                className="w-full aspect-square object-contain rounded-lg border border-white/30 transition-all duration-300 group-hover:brightness-110 bg-white/5"
                loading="lazy"
              />
              {allowExpand && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-gray-800 font-medium text-xs">
                    🔍 Expand
                  </div>
                </div>
              )}
            </div>
            <div className="text-white/60 text-xs mt-2 text-center">
              Vision {imageIndex + 1}
            </div>
          </div>
        ) : showEmptyBoxes ? (
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-3 border border-white/10 aspect-square flex items-center justify-center hover:bg-white/10 transition-all duration-300">
            <span className="text-white/40 text-xs">Add Vision</span>
          </div>
        ) : null}
      </div>
    );
  };

  if (side === 'grid') {
    return (
      <>
        <div className={`grid grid-cols-2 gap-10 ${className}`}>
          {offsets.map((offset) => (
            <VisionImage key={offset} offset={offset} />
          ))}
        </div>
        
        {/* Expanded Image Modal - Mobile Grid */}
        {expandedImage && (
          <div 
            className="fixed inset-0 bg-black flex items-center justify-center z-50"
            onClick={() => setExpandedImage(null)}
          >
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 text-3xl font-bold z-10 bg-black/50 rounded-full w-12 h-12 flex items-center justify-center"
            >
              ×
            </button>
            <img 
              src={enhanceImageUrl(expandedImage.url)} 
              alt={`Vision ${expandedImage.index + 1} - Full Resolution`}
              className="max-w-[95vw] max-h-[95vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-4 left-4 right-4 bg-black/80 text-white p-3 rounded-lg text-center">
              <h3 className="font-medium text-lg">Vision {expandedImage.index + 1}</h3>
              <p className="text-xs text-gray-300 mt-1">Tap outside to close</p>
            </div>
          </div>
        )}
      </>
    );
  }

  // For left/right sides
  return (
    <>
      <div className={`space-y-10 hidden md:block ${className}`}>
        {offsets.map((offset) => (
          <VisionImage key={offset} offset={offset} />
        ))}
      </div>
      
      {/* Full-Screen High Resolution Image Modal */}
      {expandedImage && (
        <div 
          className="fixed inset-0 bg-black flex items-center justify-center z-50"
          onClick={() => setExpandedImage(null)}
        >
          <button
            onClick={() => setExpandedImage(null)}
            className="absolute top-6 right-6 text-white hover:text-gray-300 text-4xl font-bold z-10 bg-black/50 rounded-full w-16 h-16 flex items-center justify-center"
          >
            ×
          </button>
          <img 
            src={enhanceImageUrl(expandedImage.url)} 
            alt={`Vision ${expandedImage.index + 1} - Full Resolution`}
            className="max-w-[95vw] max-h-[95vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-6 right-6 bg-black/80 text-white p-4 rounded-lg text-center">
            <h3 className="font-medium text-xl">Vision {expandedImage.index + 1} - Full Resolution</h3>
            <p className="text-sm text-gray-300 mt-1">Click outside to close • ESC to exit</p>
          </div>
        </div>
      )}
      </>
    );
};

export default VisionBoard;